import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Slider from '@radix-ui/react-slider';
import { Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import {
  RANGE_SPECS,
  formatRangeBound,
  formatRangeValue,
  isRangeActive,
  summarizeRange,
  type CategoricalFilterKey,
  type CategoricalFilters,
  type RangeFilterKey,
  type RangeFilters,
  type RangeSpec,
  type RangeValue,
} from './screener-filters.js';
import {
  MARKET_NAMES,
  CLINICAL_SPECIALISM_LABEL,
  SPECIALTY_LABEL,
  SPECIALTY_TOKENS,
  SUBSEGMENT_FILTER_OPTIONS,
} from '@hc/shared';

// Sub-segment filter is hierarchical: the canonical-17 parents with their fine
// specialty children (nursing home / residential care / …) indented beneath the
// 3 expanded parents. One dropdown, one mixed selection set (labels ∪ tokens).
const subsegmentLabelFor = (v: string): string =>
  SPECIALTY_TOKENS.has(v) ? (SPECIALTY_LABEL[v] ?? v) : v;
const subsegmentDepthFor = (v: string): number => (SPECIALTY_TOKENS.has(v) ? 1 : 0);
import { useGlobalCurrency } from './store.js';
import type { ScreenerFacets } from '../api/screener-client.js';

// Modal-based filter UI for the platform-wide Screener — Saxo-style:
// centered title, three tabs (Descriptive / Fundamental / Technical),
// collapsible filter rows with pill-style value chips, "Done (N)" with
// the result count packed into the primary action.
//
// Categoricals are driven by /screener/facets (dynamic option lists);
// ranges are driven by RANGE_SPECS (static min/max + step semantics).
// Each filter is assigned a home tab via TAB_LAYOUT below.

const CATEGORICAL_LABELS: Record<CategoricalFilterKey, string> = {
  country: 'Country',
  region: 'Region',
  subsegment: 'Sub-segment',
  clinicalSpecialism: 'Specialism',
  backing: 'Backing',
  ownershipType: 'Ownership type',
  tenure: 'Premises',
  qualityTier: 'Service quality tier',
};

const RANGE_LABELS: Record<RangeFilterKey, string> = {
  turnover: 'Revenue estimate',
  netAssets: 'Net assets',
  employees: 'Employees',
  revenueConfidence: 'Revenue confidence',
  originationSignal: 'Origination signal',
  sellerReadiness: 'Seller Readiness Score',
};

type Tab = 'descriptive' | 'fundamental' | 'technical' | 'signals';

const TAB_ORDER: readonly Tab[] = ['descriptive', 'fundamental', 'technical', 'signals'];

const TAB_LABELS: Record<Tab, string> = {
  descriptive: 'Descriptive',
  fundamental: 'Fundamental',
  technical: 'Technical',
  signals: 'Signals',
};

/** Which filters live on which tab. Categoricals + ranges are merged
 *  in display order so the user sees a single logical group per tab
 *  rather than a "all chips first, all sliders second" split. */
const TAB_LAYOUT: Record<
  Tab,
  {
    categoricals: readonly CategoricalFilterKey[];
    ranges: readonly RangeFilterKey[];
  }
> = {
  descriptive: {
    // 'backing' is not a standalone row — it's folded into the composite
    // "Ownership" row (rendered for the 'ownershipType' key) as the Independence
    // sub-group, so the two axes live under one filter.
    // 'tenure' (who holds the building) sits beside Ownership (who holds the company).
    categoricals: [
      'country',
      'region',
      'subsegment',
      'clinicalSpecialism',
      'ownershipType',
      'tenure',
    ],
    ranges: [],
  },
  fundamental: {
    categoricals: [],
    ranges: ['turnover', 'netAssets', 'employees'],
  },
  technical: {
    categoricals: ['qualityTier'],
    ranges: ['revenueConfidence'],
  },
  // Corryk's two proprietary entity scores — Origination Signal (attractiveness)
  // + Seller-Readiness (gettability) — get their own tab rather than living under
  // the data-quality "Technical" group.
  signals: {
    categoricals: [],
    ranges: ['originationSignal', 'sellerReadiness'],
  },
};

export interface ScreenerFilterModalV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoricals: CategoricalFilters;
  setCategorical: (key: CategoricalFilterKey, next: Set<string>) => void;
  ranges: RangeFilters;
  setRange: (key: RangeFilterKey, next: RangeValue) => void;
  facets: ScreenerFacets | null;
  resultCount: number;
  totalCount: number;
  onClearAll: () => void;
}

export function ScreenerFilterModalV2({
  open,
  onOpenChange,
  categoricals,
  setCategorical,
  ranges,
  setRange,
  facets,
  resultCount,
  totalCount,
  onClearAll,
}: ScreenerFilterModalV2Props) {
  const [tab, setTab] = useState<Tab>('descriptive');
  // Each time the modal re-opens, return to Descriptive so the user
  // doesn't land on an unrelated tab from a previous session.
  useEffect(() => {
    if (open) setTab('descriptive');
  }, [open]);

  const layout = TAB_LAYOUT[tab];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 9499,
          }}
        />
        <Dialog.Content
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(560px, 92vw)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 4,
            zIndex: 9500,
            overflow: 'hidden',
          }}
        >
          {/* Header — centered title, X-close absolute on the right.
              Grid layout keeps the title visually centered regardless
              of the close-button's intrinsic width. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 36px',
              alignItems: 'center',
              padding: 'var(--s-3)',
              borderBottom: '1px solid var(--hairline)',
            }}
          >
            <span />
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--t-section)',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              Add filters
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 2,
                  color: 'var(--text-secondary)',
                  justifySelf: 'end',
                }}
              >
                <X size={14} />
              </button>
            </Dialog.Close>
          </div>

          {/* Tab strip — Descriptive / Fundamental / Technical / Signals */}
          <div
            role="tablist"
            style={{
              display: 'flex',
              gap: 0,
              padding: '0 var(--s-3)',
              borderBottom: '1px solid var(--hairline)',
            }}
          >
            {TAB_ORDER.map((id) => (
              <TabBtn key={id} active={tab === id} onClick={() => setTab(id)}>
                {TAB_LABELS[id]}
              </TabBtn>
            ))}
          </div>

          {/* Body — only renders filters mapped to the active tab. */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 'var(--s-3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s-2)',
            }}
          >
            {layout.categoricals.map((key) =>
              key === 'region' ? (
                <RegionFilterRow
                  key={key}
                  label={CATEGORICAL_LABELS[key]}
                  regionsByCountry={facets?.regionsByCountry ?? {}}
                  selectedCountries={categoricals.country}
                  selected={categoricals.region}
                  onChange={(next) => setCategorical('region', next)}
                />
              ) : key === 'ownershipType' ? (
                // One "Ownership" row holding both axes (backing + control).
                // Independence drops 'Corporate-owned' — it's the same companies
                // as Control's 'Corporate-owned (75%+)', so the axis reads as
                // stands-alone vs in-a-roll-up; corporate-owned lives in Control.
                <OwnershipFilterRow
                  key={key}
                  backingOptions={(facets?.backing ?? []).filter((b) => b !== 'Corporate-owned')}
                  backingSelected={categoricals.backing}
                  onBackingChange={(next) => setCategorical('backing', next)}
                  controlOptions={facets?.ownershipType ?? []}
                  controlSelected={categoricals.ownershipType}
                  onControlChange={(next) => setCategorical('ownershipType', next)}
                />
              ) : (
                <CategoricalRow
                  key={key}
                  label={CATEGORICAL_LABELS[key]}
                  // Sub-segment is hierarchical (canonical-17 + fine specialty
                  // children); every other categorical uses its /facets list.
                  options={key === 'subsegment' ? SUBSEGMENT_FILTER_OPTIONS : (facets?.[key] ?? [])}
                  selected={categoricals[key]}
                  onChange={(next) => setCategorical(key, next)}
                  labelFor={
                    key === 'subsegment'
                      ? subsegmentLabelFor
                      : key === 'clinicalSpecialism'
                        ? (t) => CLINICAL_SPECIALISM_LABEL[t] ?? t
                        : undefined
                  }
                  depthFor={key === 'subsegment' ? subsegmentDepthFor : undefined}
                />
              ),
            )}
            {layout.ranges.map((rangeKey) => {
              const spec = RANGE_SPECS.find((s) => s.key === rangeKey);
              if (!spec) return null;
              return (
                <RangeRow
                  key={spec.key}
                  spec={spec}
                  label={RANGE_LABELS[spec.key]}
                  value={ranges[spec.key]}
                  onChange={(next) => setRange(spec.key, next)}
                />
              );
            })}
          </div>

          {/* Footer — Reset on the left, "Done (N of M)" on the right
              with the result count packed into the primary action. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              padding: 'var(--s-3)',
              borderTop: '1px solid var(--hairline)',
              background: 'var(--surface)',
            }}
          >
            <button
              type="button"
              onClick={onClearAll}
              style={{
                fontSize: 'var(--t-body)',
                padding: '4px 12px',
                background: 'transparent',
                border: '1px solid var(--hairline)',
                borderRadius: 2,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              Reset all filters
            </button>
            <div style={{ flex: 1 }} />
            <Dialog.Close asChild>
              <button
                type="button"
                data-accent="true"
                style={{
                  fontSize: 'var(--t-body)',
                  padding: '4px 16px',
                  background: 'var(--accent)',
                  border: '1px solid var(--accent)',
                  borderRadius: 2,
                  cursor: 'pointer',
                  color: 'var(--bg)',
                  fontWeight: 600,
                }}
              >
                Done ({resultCount.toLocaleString('en-GB')} of {totalCount.toLocaleString('en-GB')})
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Saxo-style pill chip that displays a filter's current value on its
 *  collapsed row. Outlined neutral when the filter is "All" (inactive);
 *  filled accent-tint with accent border when an active selection
 *  exists. Truncates long summaries to keep the row layout stable. */
function ValuePill({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--t-body)',
        padding: '2px var(--s-2)',
        // Border removed — active uses the warm accent fill, inactive
        // is just text. No outline.
        background: active ? 'var(--color-accent-100)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-tertiary)',
        maxWidth: 220,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '8px var(--s-3)',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        fontSize: 'var(--t-body)',
        marginBottom: -1, // overlap the container's border-bottom hairline
      }}
    >
      {children}
    </button>
  );
}

function CategoricalRow({
  label,
  options,
  selected,
  onChange,
  labelFor = (v) => v,
  depthFor,
}: {
  label: string;
  options: readonly string[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  labelFor?: (v: string) => string;
  /** Optional indent level per option (0 = top level) — drives the hierarchical
   *  Sub-segment list (parents at 0, fine specialty children at 1). */
  depthFor?: (v: string) => number;
}) {
  const [open, setOpen] = useState(selected.size > 0);
  // Saxo-style summary: "All" | "Acme" | "Acme +11".
  const summary =
    selected.size === 0
      ? 'All'
      : selected.size === 1
        ? labelFor(Array.from(selected)[0]!)
        : `${labelFor(Array.from(selected)[0]!)} +${selected.size - 1}`;
  return (
    <div
      style={{
        // Single hairline at the top of each filter row — no other borders.
        borderTop: '1px solid var(--hairline)',
        background: 'var(--surface)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          width: '100%',
          padding: '6px var(--s-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <ValuePill active={selected.size > 0}>{summary}</ValuePill>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div style={{ padding: '4px var(--s-2) var(--s-2)' }}>
          {options.length === 0 ? (
            <span style={{ fontSize: 'var(--t-body)', color: 'var(--text-tertiary)' }}>
              no values
            </span>
          ) : (
            <CheckboxList
              options={options}
              selected={selected}
              onChange={onChange}
              labelFor={labelFor}
              depthFor={depthFor}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Composite "Ownership" filter — two axes in one row. Independence (the
 *  backing axis: stands alone vs in a roll-up / corporate-owned) and Control
 *  (the shareholding structure). They compose: Independence = Independent gives
 *  the acquirable universe; Control refines it. Writes to two filter keys
 *  (backing + ownershipType), AND-ed server-side. */
function OwnershipFilterRow({
  backingOptions,
  backingSelected,
  onBackingChange,
  controlOptions,
  controlSelected,
  onControlChange,
}: {
  backingOptions: readonly string[];
  backingSelected: ReadonlySet<string>;
  onBackingChange: (next: Set<string>) => void;
  controlOptions: readonly string[];
  controlSelected: ReadonlySet<string>;
  onControlChange: (next: Set<string>) => void;
}) {
  const total = backingSelected.size + controlSelected.size;
  const [open, setOpen] = useState(total > 0);
  const summary = total === 0 ? 'All' : `${total} selected`;
  const subLabel: React.CSSProperties = {
    fontSize: 'var(--t-meta)',
    fontWeight: 600,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '2px var(--s-2) 4px',
  };
  return (
    <div style={{ borderTop: '1px solid var(--hairline)', background: 'var(--surface)' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          width: '100%',
          padding: '6px var(--s-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ flex: 1 }}>Ownership</span>
        <ValuePill active={total > 0}>{summary}</ValuePill>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div style={{ padding: '4px var(--s-2) var(--s-2)' }}>
          <div style={subLabel}>Independence</div>
          <CheckboxList
            options={backingOptions}
            selected={backingSelected}
            onChange={onBackingChange}
          />
          <div style={{ ...subLabel, marginTop: 'var(--s-2)' }}>Control</div>
          <CheckboxList
            options={controlOptions}
            selected={controlSelected}
            onChange={onControlChange}
          />
        </div>
      )}
    </div>
  );
}

/** Region picker — like CategoricalRow but regions are grouped under their
 *  country and only the countries the user has selected are shown (all of
 *  them when no country filter is set). Selection is by region name. */
function RegionFilterRow({
  label,
  regionsByCountry,
  selectedCountries,
  selected,
  onChange,
}: {
  label: string;
  regionsByCountry: Record<string, readonly string[]>;
  selectedCountries: ReadonlySet<string>;
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(selected.size > 0);
  const visibleCountries = useMemo(() => {
    const all = Object.keys(regionsByCountry).sort();
    return selectedCountries.size > 0 ? all.filter((c) => selectedCountries.has(c)) : all;
  }, [regionsByCountry, selectedCountries]);
  const summary =
    selected.size === 0
      ? 'All'
      : selected.size === 1
        ? Array.from(selected)[0]!
        : `${Array.from(selected)[0]!} +${selected.size - 1}`;
  const allActive = selected.size === 0;
  return (
    <div style={{ borderTop: '1px solid var(--hairline)', background: 'var(--surface)' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          width: '100%',
          padding: '6px var(--s-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <ValuePill active={selected.size > 0}>{summary}</ValuePill>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div style={{ padding: '4px var(--s-2) var(--s-2)' }}>
          {visibleCountries.length === 0 ? (
            <span style={{ fontSize: 'var(--t-body)', color: 'var(--text-tertiary)' }}>
              no values
            </span>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--s-1)',
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              <CheckboxRow
                checked={allActive}
                label="All"
                onToggle={() => {
                  if (!allActive) onChange(new Set());
                }}
              />
              {visibleCountries.map((c) => (
                <div key={c}>
                  <div
                    style={{
                      padding: '4px var(--s-2) 2px',
                      fontSize: 'var(--t-meta)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    {MARKET_NAMES[c as keyof typeof MARKET_NAMES] ?? c}
                  </div>
                  {(regionsByCountry[c] ?? []).map((region) => {
                    const checked = selected.has(region);
                    return (
                      <CheckboxRow
                        key={`${c}:${region}`}
                        checked={checked}
                        label={region}
                        onToggle={() => {
                          const next = new Set(selected);
                          if (checked) next.delete(region);
                          else next.add(region);
                          onChange(next);
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Saxo-style vertical multi-select. "All" toggle, optional inline
 *  search (auto-shows when there are more than 8 options), scrollable
 *  checkbox list. */
function CheckboxList({
  options,
  selected,
  onChange,
  maxHeight = 220,
  labelFor = (v) => v,
  depthFor,
}: {
  options: readonly string[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  maxHeight?: number;
  labelFor?: (v: string) => string;
  depthFor?: (v: string) => number;
}) {
  const [query, setQuery] = useState('');
  const showSearch = options.length > 8;
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((opt) => labelFor(opt).toLowerCase().includes(q));
  }, [options, query, labelFor]);
  const allActive = selected.size === 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      {showSearch && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-1)',
            padding: '2px var(--s-2)',
            // No border on the inline search — sits flush in the panel.
            background: 'var(--surface)',
          }}
        >
          <Search size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            placeholder="Search"
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--t-body)',
              padding: '2px 0',
              color: 'var(--text-primary)',
            }}
          />
          {query && (
            <X
              size={10}
              onClick={() => setQuery('')}
              style={{ cursor: 'pointer', color: 'var(--text-tertiary)' }}
            />
          )}
        </div>
      )}
      <div
        style={{
          maxHeight,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          // Box border removed — rows below are separated by their own
          // borderBottom hairlines.
          background: 'var(--surface)',
        }}
      >
        <CheckboxRow
          checked={allActive}
          label="All"
          onToggle={() => {
            if (allActive) return;
            onChange(new Set());
          }}
        />
        {filtered.map((opt) => {
          const checked = selected.has(opt);
          return (
            <CheckboxRow
              key={opt}
              checked={checked}
              label={labelFor(opt)}
              depth={depthFor?.(opt) ?? 0}
              onToggle={() => {
                const next = new Set(selected);
                if (checked) next.delete(opt);
                else next.add(opt);
                onChange(next);
              }}
            />
          );
        })}
        {filtered.length === 0 && (
          <div
            style={{
              padding: 'var(--s-2) var(--s-3)',
              fontSize: 'var(--t-body)',
              color: 'var(--text-tertiary)',
            }}
          >
            No matches
          </div>
        )}
      </div>
    </div>
  );
}

function CheckboxRow({
  checked,
  label,
  onToggle,
  depth = 0,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
  depth?: number;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        // Indent nested (fine specialty) rows under their parent sub-segment.
        padding: `4px var(--s-2) 4px calc(var(--s-2) + ${depth * 16}px)`,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'var(--t-body)',
        color: 'var(--text-primary)',
        textAlign: 'left',
        width: '100%',
        // No border between checklist rows — the checkbox indicator
        // and label spacing provide enough visual rhythm.
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          flexShrink: 0,
          borderRadius: 2,
          border: `1px solid ${checked ? 'var(--accent)' : 'var(--hairline)'}`,
          background: checked ? 'var(--accent)' : 'var(--surface)',
          color: 'var(--bg)',
        }}
      >
        {checked && <Check size={10} strokeWidth={3} />}
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  );
}

function RangeRow({
  spec,
  label,
  value,
  onChange,
}: {
  spec: RangeSpec;
  label: string;
  value: RangeValue;
  onChange: (next: RangeValue) => void;
}) {
  const ccy = useGlobalCurrency();
  // Clamp persisted values into the spec bounds. A range saved under old
  // bounds (e.g. turnover lower = 0 before the €150k floor) would otherwise
  // render below the slider's new minimum; normalise it once so the stored
  // filter matches what's shown.
  // Stop ladder (non-linear axis): the slider runs over stop indices, each
  // equal track width. idxOf maps a value to its nearest stop.
  const stops = spec.stops;
  const idxOf = (v: number) => {
    if (!stops) return 0;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < stops.length; i++) {
      const d = Math.abs(stops[i]! - v);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  };
  // Clamp into bounds, then snap to the nearest stop when there's a ladder.
  const fit = (x: number) => {
    const c = Math.min(Math.max(x, spec.min), spec.max);
    return stops ? stops[idxOf(c)]! : c;
  };
  const clamped: RangeValue = [fit(value[0]), fit(value[1])];
  // Normalise persisted out-of-bounds / off-stop values once on mount.
  useEffect(() => {
    if (clamped[0] !== value[0] || clamped[1] !== value[1]) onChange(clamped);
  }, []);
  const [open, setOpen] = useState(isRangeActive(spec, clamped));
  const summary = summarizeRange(spec, clamped, ccy);
  return (
    <div
      style={{
        // Single hairline at the top of each filter row — no other borders.
        borderTop: '1px solid var(--hairline)',
        background: 'var(--surface)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          width: '100%',
          padding: '6px var(--s-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <ValuePill active={isRangeActive(spec, clamped)}>{summary}</ValuePill>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div
          style={{
            padding: '4px var(--s-2) var(--s-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <Slider.Root
            value={stops ? [idxOf(clamped[0]), idxOf(clamped[1])] : [clamped[0], clamped[1]]}
            min={stops ? 0 : spec.min}
            max={stops ? stops.length - 1 : spec.max}
            step={stops ? 1 : spec.step}
            onValueChange={(vs) => {
              if (vs.length !== 2) return;
              onChange((stops ? [stops[vs[0]!]!, stops[vs[1]!]!] : [vs[0]!, vs[1]!]) as RangeValue);
            }}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              userSelect: 'none',
              touchAction: 'none',
              width: '100%',
              height: 20,
            }}
          >
            <Slider.Track
              style={{
                position: 'relative',
                flexGrow: 1,
                background: 'var(--hairline)',
                height: 3,
                borderRadius: 2,
              }}
            >
              <Slider.Range
                style={{ position: 'absolute', background: 'var(--accent)', height: '100%' }}
              />
            </Slider.Track>
            <Slider.Thumb
              style={{
                display: 'block',
                width: 12,
                height: 12,
                background: 'var(--surface)',
                border: '2px solid var(--accent)',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            />
            <Slider.Thumb
              style={{
                display: 'block',
                width: 12,
                height: 12,
                background: 'var(--surface)',
                border: '2px solid var(--accent)',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            />
          </Slider.Root>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--t-body)',
              color: 'var(--text-secondary)',
            }}
          >
            <span>{formatRangeBound(spec, clamped[0], 'min', ccy)}</span>
            <span>{formatRangeBound(spec, clamped[1], 'max', ccy)}</span>
          </div>
          {/* eslint-disable-next-line @typescript-eslint/no-unused-expressions */}
          {void formatRangeValue}
        </div>
      )}
    </div>
  );
}
